import { z } from 'zod';
import { leadStatusSchema } from '../roles';
/**
 * PIPELINE — het lead-Board onder Kontakte (Kommunikation PR-3; owner-akkoord
 * 26-8-2026, KOMMUNIKATION-PIPELINE-ARCHITECTUUR §1.2).
 *
 * Een `pipeline` is per tenant en per `side` (Käufer | Verkäufer | unbekannt)
 * precies één keer aanwezig; haar `stufen` zijn de kolommen van het Board. Elke
 * stufe draagt een KANONIEKE `LeadStatus`: een lead verplaatsen zet de stufe
 * ÉN die status, zodat alles wat vandaag aan `leads.status` hangt (Reifegrad,
 * KPI's, auto-send, audit) ongewijzigd blijft werken. De stufe-sleutel is wat
 * een Automation (`mail_vorlagen.trigger.art = 'pipeline'`) benoemt.
 *
 * Niet te verwarren met `schemas/pipeline.ts`: dat is de VERKOOPS-pipeline van
 * een mandaat (Vertrag → Checkliste → …), geen lead-board.
 */
// ── Vocabulaire ──────────────────────────────────────────────────────────────
/** Stufe-sleutel: stabiel, per pipeline uniek — dezelfde vorm als `trigger.stufeKey`. */
export const stufeKeySchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, { message: 'stufe_invalid' });
const stufeLabelSchema = z.string().trim().min(1).max(60);
const pipelineNameSchema = z.string().trim().min(1).max(80);
/** Optionele kolomkleur: een category-token 1–10 van het huis-palet, of niets. */
const farbeSchema = z.number().int().min(1).max(10);
/** Bovengrens van het Board per pipeline — de lijst-API kapt op 100, het Board zegt het eerlijk. */
export const PIPELINE_BOARD_MAX = 100;
// ── Schrijfcontracten ────────────────────────────────────────────────────────
export const pipelineBoardQuerySchema = z.object({ pipelineId: z.string().uuid() }).strict();
/** `PATCH /pipeline/leads/:id/stufe` — het verplaatsgebaar (alle stafrollen). */
export const leadStufeSetzenSchema = z
    .object({
    pipelineId: z.string().uuid(),
    stufeKey: stufeKeySchema,
})
    .strict();
/** Eén stufe in «Pipeline-Einstellungen» (admin). */
export const pipelineStufeEingabeSchema = z
    .object({
    key: stufeKeySchema,
    label: stufeLabelSchema,
    kanonischerStatus: leadStatusSchema,
    farbe: farbeSchema.nullable().default(null),
})
    .strict();
/**
 * `PUT /pipeline/:id/stufen` — de hele kolommenlijst in één keer, in
 * board-volgorde (`reihenfolge` = index). Minstens één stufe; sleutels uniek.
 * Een stufe die nog leads draagt kan pas weg als die leads elders staan —
 * de server weigert dat met `stufe_in_gebruik`.
 */
export const pipelineStufenSetzenSchema = z
    .object({
    stufen: z.array(pipelineStufeEingabeSchema).min(1).max(20),
})
    .strict()
    .refine((v) => new Set(v.stufen.map((s) => s.key)).size === v.stufen.length, {
    message: 'stufe_key_doppelt',
});
/** `PATCH /pipeline/:id` — naam van de pipeline (admin). */
export const pipelineUpdateSchema = z.object({ name: pipelineNameSchema }).strict();
// ── Rollen ───────────────────────────────────────────────────────────────────
/** Het Board LEZEN en erop VERPLAATSEN: elke stafrol die het CRM ziet (dossier §1.3). */
export const PIPELINE_LESEN = ['admin', 'advisor', 'backoffice', 'makler'];
/** «Pipeline-Einstellungen»: `KOMMUNIKATION_RECHTE['pipeline.verwalten']` = admin. */
export const PIPELINE_VERWALTEN = ['admin'];
// ── Seed ─────────────────────────────────────────────────────────────────────
/**
 * De standaard-stufen: de tien `LeadStatus`-waarden in funnel-volgorde, met
 * `key` = statusnaam. Dat is geen gemak maar de MIGRATIE-BRUG: de oude kolommen
 * `mail_vorlagen.triggerStatus/triggerSide` worden verliesvrij
 * `trigger = { art: 'pipeline', stufeKey: <status>, side }`, en een lead die
 * nog nooit op het Board stond valt in de kolom van zijn status. Gespiegeld in
 * migratie `20260829120000_lead_pipeline` en `seed.ts`;
 * `pipeline-seed.contract.test.ts` toetst de drie kopieën tegen elkaar.
 */
export const STANDARD_STUFEN = [
    { key: 'new', label: 'Neu', kanonischerStatus: 'new', farbe: null },
    {
        key: 'consult_requested',
        label: 'Beratung angefragt',
        kanonischerStatus: 'consult_requested',
        farbe: null,
    },
    { key: 'contacted', label: 'Kontaktiert', kanonischerStatus: 'contacted', farbe: null },
    { key: 'qualified', label: 'Qualifiziert', kanonischerStatus: 'qualified', farbe: null },
    { key: 'invited', label: 'Eingeladen', kanonischerStatus: 'invited', farbe: null },
    {
        key: 'dossier_started',
        label: 'Dossier begonnen',
        kanonischerStatus: 'dossier_started',
        farbe: null,
    },
    {
        key: 'dossier_completed',
        label: 'Dossier komplett',
        kanonischerStatus: 'dossier_completed',
        farbe: null,
    },
    { key: 'matched', label: 'Matching', kanonischerStatus: 'matched', farbe: null },
    { key: 'won', label: 'Gewonnen', kanonischerStatus: 'won', farbe: 9 },
    { key: 'lost', label: 'Verloren', kanonischerStatus: 'lost', farbe: 10 },
];
/**
 * Dossier §1.2: Finavest één pipeline (hypotheek — leads zonder koop/verkoop-
 * kant, `side = unknown`), MLG Verkäufer + Käufer, holding en gu geen.
 */
export const PIPELINE_SEED = [
    { slug: 'finavest', side: 'unknown', name: 'Hypothek' },
    { slug: 'mlg', side: 'seller', name: 'Verkäufer' },
    { slug: 'mlg', side: 'buyer', name: 'Käufer' },
];
/** Sleutel-voor-status: de stufe die een lead zonder Board-plaats krijgt. */
export function stufeFuerStatus(stufen, status) {
    const kandidaten = stufen
        .filter((s) => s.kanonischerStatus === status)
        .sort((a, b) => a.reihenfolge - b.reihenfolge);
    return kandidaten[0] ?? null;
}
/** De kolom van een kaart: de expliciete stufe als ze nog bestaat, anders die van de status. */
export function kolomVoorKarte(stufen, karte) {
    if (karte.pipelineStufeKey) {
        const expliciet = stufen.find((s) => s.key === karte.pipelineStufeKey);
        if (expliciet)
            return expliciet;
    }
    return stufeFuerStatus(stufen, karte.status);
}
